import { memo, useMemo, type ReactElement, type ReactNode } from 'react'
import { isDefined } from '@terros/common'
import { Box, Stack } from '@mantine/core'
import { useTranslation } from '@front/model'
import { useTeamHierarchyContext } from '../TeamHierarchyContext'
import { ViewTeamPanel } from '../../viewTeam'
import viewportClasses from '../../../graphViewport/graphViewport.module.css'
import { ZoomControls, useGraphViewport } from '../../../graphViewport'
import { VisibilityLegend } from './VisibilityLegend'
import { TeamFocusRow } from './TeamFocusRow'
import { TeamConnectionsLayer } from './TeamConnectionsLayer'

export const TeamHierarchyDisplay = ({ footer }: { footer?: ReactNode }): ReactElement => {
  return (
    <Box className='flex min-h-0 flex-1 overflow-hidden'>
      <Box className='flex min-h-0 min-w-0 flex-1 flex-col'>
        <HierarchyViewport />
        {footer && (
          <Box pb='xl' px='xl'>
            {footer}
          </Box>
        )}
      </Box>
      <TeamPanel />
    </Box>
  )
}

const HierarchyViewport = (): ReactElement => {
  const { t } = useTranslation()
  const { layout, viewTeam, visibleTeamIds } = useTeamHierarchyContext()
  const focusId = layout.focusTeam?.teamId ?? null
  const { containerRef, contentRef, zoom, pan, isPanning, layoutReady, zoomIn, zoomOut, resetZoom, ...pointer } =
    useGraphViewport({
      focusId,
      hasContent: isDefined(focusId),
      recenterKey: layout.layoutKey,
      panelOpen: isDefined(viewTeam),
    })

  return (
    <div
      ref={containerRef}
      aria-label={t('Team hierarchy viewport')}
      className={viewportClasses.viewport}
      data-panning={isPanning}
      role='region'
      tabIndex={0}
      onLostPointerCapture={pointer.onPointerEnd}
      onPointerCancel={pointer.onPointerEnd}
      onPointerDown={pointer.onPointerDown}
      onPointerMove={pointer.onPointerMove}
      onPointerUp={pointer.onPointerEnd}
    >
      <ZoomControls zoom={zoom} onZoomIn={zoomIn} onZoomOut={zoomOut} onReset={resetZoom} />
      {isDefined(visibleTeamIds) && <VisibilityLegend />}
      <div
        className={viewportClasses.panLayer}
        style={{
          opacity: layoutReady ? 1 : 0,
          transform: `translate3d(${pan.x}px,${pan.y}px,0)`,
          transition: isPanning ? 'none' : 'transform 200ms ease',
        }}
      >
        <div
          ref={contentRef}
          className={viewportClasses.contentLayer}
          style={{
            transform: `scale(${zoom})`,
            transition: isPanning ? 'none' : 'transform 200ms ease',
          }}
        >
          <HierarchyCanvas />
        </div>
      </div>
    </div>
  )
}

const HierarchyCanvas = memo(function HierarchyCanvas(): ReactElement {
  const { layout } = useTeamHierarchyContext()

  return (
    <Stack gap='sm' className='py-4xl px-xl'>
      <Box className='gap-6xl relative flex flex-col'>
        <TeamConnectionsLayer />
        <Box className='gap-6xl flex flex-col'>
          {layout.rows.map((row) => (
            <TeamFocusRow key={row.rowId} row={row} />
          ))}
        </Box>
      </Box>
    </Stack>
  )
})

const TeamPanel = (): ReactElement => {
  const { viewTeam, openTeamPanel, closeTeamPanel, teams: allTeams, company } = useTeamHierarchyContext()
  const viewingTeam = useMemo(() => allTeams.find((t) => t.teamId === viewTeam), [allTeams, viewTeam])

  return (
    <ViewTeamPanel
      company={company}
      opened={isDefined(viewTeam)}
      team={viewingTeam}
      onNavigateTeam={openTeamPanel}
      onClose={closeTeamPanel}
    />
  )
}
